/**
 * Bulk CSV upload for PSA, Tech Assessment, Tech Panel, Offer, and Video Bot banks.
 */
import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get, v1Upload } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Upload, Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { IQ_CATEGORIES } from '@/lib/reportConstants';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const STATUSES = ['draft', 'active', 'retired'];
const NONE = '__none__';

const PSA_TEMPLATE = [
  'question,option_a,option_b,option_c,option_d,correct_option,difficulty,topic,tags,status,image_url',
  '"What is the time complexity of binary search?","O(n)","O(log n)","O(n log n)","O(1)",B,medium,Computer & IT Fundamentals,"SE_Dev",draft,',
  '"Refer to the diagram. Which component fans out to async consumers?","Load balancer","Message broker","API gateway","CDN",B,medium,Logic,"SE_Dev",draft,https://example.com/diagram.png',
].join('\n');

const TECH_TEMPLATE = [
  'question,option_a,option_b,option_c,option_d,correct_option,difficulty,topic,role,department,status',
  '"Which hook runs after every render?","useMemo","useEffect","useRef","useState",B,medium,Programming & Frameworks,Software Engineer,Engineering,draft',
].join('\n');

const PANEL_TEMPLATE = [
  'question,model_answer,red_flags,difficulty,topic,department,status',
  '"How would you design a rate limiter for a public API?","Token bucket or sliding window with Redis; discuss burst vs sustained rate and client fairness.","No mention of distributed state; only client-side throttling.",medium,Technology Foundation & Architecture,Engineering,draft',
].join('\n');

const OFFER_TEMPLATE = [
  'question,model_answer,red_flags,topic,status',
  '"What compensation range are you targeting?","Candidate shares a clear, researched range and can explain trade-offs vs equity/benefits.","Vague or extreme numbers with no rationale; unwilling to discuss total rewards.",Compensation,draft',
].join('\n');

const VIDEOBOT_TEMPLATE = [
  'question,key_concepts,acceptable_variants,red_flags,layer1,layer2,primary_focus,prep_seconds,record_seconds,retake_attempts,difficulty,tags,status,role',
  '"Describe a time you resolved a production incident.","Root cause|Mitigation|Communication","Blameless postmortem","No ownership|Blame only",40,60,"Structure|Relevance",30,90,0,medium,"SE_Dev",draft,Software Engineer',
].join('\n');

// Keep in sync with backend DUPLICATE_QUESTION_ERROR (question_bank_import.py).
const DUPLICATE_QUESTION_ERROR = 'This question already exists. Duplicate questions cannot be created.';
const DUPLICATE_IN_FILE_ERROR = 'Duplicate question text within this file';

function extractDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d.map((e) => e.msg).join(' · ');
  return err?.message || fallback;
}

function previewZeroValidToastMessage(result) {
  const errors = (result.rows || []).flatMap((row) => row.errors || []);
  if (errors.some((e) => e === DUPLICATE_QUESTION_ERROR || e.includes('already exists'))) {
    return DUPLICATE_QUESTION_ERROR;
  }
  if (errors.some((e) => e === DUPLICATE_IN_FILE_ERROR)) {
    return DUPLICATE_IN_FILE_ERROR;
  }
  return 'No valid rows — fix errors and try again';
}

function downloadTemplate(kind) {
  const content = kind === 'tech'
    ? TECH_TEMPLATE
    : kind === 'panel'
      ? PANEL_TEMPLATE
      : kind === 'offer'
        ? OFFER_TEMPLATE
        : kind === 'videobot'
          ? VIDEOBOT_TEMPLATE
          : PSA_TEMPLATE;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = kind === 'tech'
    ? 'tech-question-bank-template.csv'
    : kind === 'panel'
      ? 'tech-panel-question-bank-template.csv'
      : kind === 'offer'
        ? 'offer-question-bank-template.csv'
        : kind === 'videobot'
          ? 'videobot-question-bank-template.csv'
          : 'iq-assessment-question-bank-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function bankTitle(kind) {
  if (kind === 'tech') return 'Tech Assessment';
  if (kind === 'panel') return 'Tech Panel';
  if (kind === 'offer') return 'Offer';
  if (kind === 'videobot') return 'Video Bot';
  return 'IQ Assessment';
}

export default function QuestionBankBulkUpload({
  kind,
  open,
  onOpenChange,
  onImported,
  roles = [],
  departments = [],
}) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [defaults, setDefaults] = useState({
    difficulty: 'medium',
    topic: '',
    tags: '',
    status: 'draft',
    role_id: '',
    department_id: '',
    prep_seconds: '30',
    record_seconds: '90',
    retake_attempts: '0',
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const isTech = kind === 'tech';
  const isPanel = kind === 'panel';
  const isOffer = kind === 'offer';
  const isVideobot = kind === 'videobot';
  const isPsa = kind === 'psa' || (!isTech && !isPanel && !isOffer && !isVideobot);
  const usesWorkCategories = isTech || isPanel;
  const usesDifficulty = !isOffer;
  const usesIqCategory = kind === 'psa' || (!usesWorkCategories && !isOffer && !isVideobot);

  const basePath = kind === 'tech'
    ? '/questions/tech'
    : kind === 'panel'
      ? '/questions/panel'
      : kind === 'offer'
        ? '/questions/offer'
        : kind === 'videobot'
          ? '/questions/videobot'
          : '/questions/mcq';

  const { data: workCategories = [] } = useQuery({
    queryKey: ['v1-work-categories', defaults.department_id],
    queryFn: () => v1Get(`/questions/work-categories?department_id=${defaults.department_id}`),
    enabled: usesWorkCategories && !!defaults.department_id,
  });

  const reset = () => {
    setFile(null);
    setPreview(null);
    setDefaults({
      difficulty: 'medium',
      topic: '',
      tags: '',
      status: 'draft',
      role_id: '',
      department_id: '',
      prep_seconds: '30',
      record_seconds: '90',
      retake_attempts: '0',
    });
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const buildExtraFields = () => {
    const fields = {
      status: defaults.status,
    };
    if (usesDifficulty) fields.difficulty = defaults.difficulty;
    if (defaults.topic.trim() && (usesIqCategory || usesWorkCategories || isOffer)) {
      fields.topic = defaults.topic.trim();
    }
    if ((usesIqCategory || isVideobot) && defaults.tags.trim()) {
      fields.tags = defaults.tags.trim();
    }
    if ((isTech || isVideobot) && defaults.role_id) fields.role_id = defaults.role_id;
    if (usesWorkCategories && defaults.department_id) {
      fields.department_id = defaults.department_id;
    }
    if (isVideobot) {
      if (defaults.prep_seconds.trim()) fields.prep_seconds = defaults.prep_seconds.trim();
      if (defaults.record_seconds.trim()) fields.record_seconds = defaults.record_seconds.trim();
      if (defaults.retake_attempts.trim()) fields.retake_attempts = defaults.retake_attempts.trim();
    }
    return fields;
  };

  const runPreview = async () => {
    if (!file) return toast.error('Choose a CSV file first');
    setLoading(true);
    try {
      const result = await v1Upload(`${basePath}/import/preview`, file, buildExtraFields());
      setPreview(result);
      if (result.valid_count === 0) {
        toast.error(previewZeroValidToastMessage(result));
      } else {
        toast.success(`${result.valid_count} of ${result.total_rows} rows ready to import`);
      }
    } catch (e) {
      toast.error(extractDetail(e, 'Preview failed'));
    } finally {
      setLoading(false);
    }
  };

  const runImport = async () => {
    if (!file || !preview?.valid_count) return;
    setImporting(true);
    try {
      const result = await v1Upload(`${basePath}/import`, file, buildExtraFields());
      toast.success(`Imported ${result.imported_count} question${result.imported_count === 1 ? '' : 's'}`);
      onImported?.(result);
      handleClose(false);
    } catch (e) {
      toast.error(extractDetail(e, 'Import failed'));
    } finally {
      setImporting(false);
    }
  };

  const requiredColsHint = isPanel || isOffer
    ? 'UTF-8 CSV · required column: question (model_answer / red_flags optional)'
    : isVideobot
      ? 'UTF-8 CSV · required columns: question, key_concepts, role (or set Default role below)'
      : isPsa
        ? 'UTF-8 CSV · required columns: question, option_a–d, correct_option · optional image_url (HTTPS png/jpeg/webp)'
        : 'UTF-8 CSV · required columns: question, option_a–d, correct_option';

  const description = (() => {
    if (isVideobot) {
      return ' Upload a CSV with one question per row. key_concepts, acceptable_variants, red_flags, and primary_focus use pipe (|) separators. Layer weights must sum to 100 (defaults 40/60).';
    }
    if (isOffer) {
      return ' Upload a CSV with one offer checklist question per row. Topic is optional free text.';
    }
    if (usesWorkCategories) {
      return ' Department and category are required (set below as defaults or per row); category must be a Work Assessment category of the row\u2019s department.';
    }
    return ' Every row needs an IQ category — set a default below or fill the topic column. Optional image_url (HTTPS) attaches a diagram/image to the question. Rows with an unknown category are flagged in the preview.';
  })();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${kind}-bulk-upload-dialog`}>
        <DialogHeader>
          <DialogTitle>Bulk upload {bankTitle(kind)} questions</DialogTitle>
          <DialogDescription>
            Upload a CSV with one question per row. Questions are created as drafts unless you set status otherwise.
            {description}
            {isTech
              ? ' Role is optional — the CSV may omit the role column or leave it blank (single role id/code/name).'
              : isVideobot
                ? ' Role is required — set Default role below or provide a role column (id/code/name) per row.'
                : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => downloadTemplate(kind)} data-testid={`${kind}-bulk-template`}>
              <Download className="h-3.5 w-3.5" /> Download template
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {usesDifficulty && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Default difficulty</label>
                <Select value={defaults.difficulty} onValueChange={(v) => setDefaults({ ...defaults, difficulty: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DIFFICULTIES.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Default status</label>
              <Select value={defaults.status} onValueChange={(v) => setDefaults({ ...defaults, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {usesIqCategory && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Default IQ category</label>
                <Select value={defaults.topic || NONE} onValueChange={(v) => setDefaults({ ...defaults, topic: v === NONE ? '' : v })}>
                  <SelectTrigger data-testid="bulk-default-topic"><SelectValue placeholder="None (use CSV column)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None (use CSV column)</SelectItem>
                    {IQ_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isOffer && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Default topic (optional)</label>
                <Input
                  value={defaults.topic}
                  onChange={(e) => setDefaults({ ...defaults, topic: e.target.value })}
                  placeholder="e.g. Compensation"
                  data-testid="bulk-default-topic"
                />
              </div>
            )}
            {(usesIqCategory || isVideobot) && (
              <div className={usesIqCategory ? 'md:col-span-3' : 'md:col-span-2'}>
                <label className="text-xs font-medium text-muted-foreground">Default tags (comma-separated)</label>
                <Input value={defaults.tags} onChange={(e) => setDefaults({ ...defaults, tags: e.target.value })} placeholder="SE_Dev, Security" />
              </div>
            )}
            {isVideobot && (
              <>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Default role (required)</label>
                  <Select value={defaults.role_id || NONE} onValueChange={(v) => setDefaults({ ...defaults, role_id: v === NONE ? '' : v })}>
                    <SelectTrigger data-testid="bulk-default-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None (use CSV column)</SelectItem>
                      {roles.filter((r) => r.status === 'active').map((r) => (
                        <SelectItem key={r.id} value={r.id}>{formatMasterOptionLabel(r)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Default prep seconds</label>
                  <Input
                    type="number"
                    min={0}
                    max={600}
                    value={defaults.prep_seconds}
                    onChange={(e) => setDefaults({ ...defaults, prep_seconds: e.target.value })}
                    data-testid="bulk-default-prep"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Default record seconds</label>
                  <Input
                    type="number"
                    min={15}
                    max={1800}
                    value={defaults.record_seconds}
                    onChange={(e) => setDefaults({ ...defaults, record_seconds: e.target.value })}
                    data-testid="bulk-default-record"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Default retakes</label>
                  <Input
                    type="number"
                    min={0}
                    max={5}
                    value={defaults.retake_attempts}
                    onChange={(e) => setDefaults({ ...defaults, retake_attempts: e.target.value })}
                    data-testid="bulk-default-retakes"
                  />
                </div>
              </>
            )}
            {usesWorkCategories && (
              <>
                {isTech && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Default role</label>
                    <Select value={defaults.role_id || NONE} onValueChange={(v) => setDefaults({ ...defaults, role_id: v === NONE ? '' : v })}>
                      <SelectTrigger data-testid="bulk-default-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>None (use CSV column)</SelectItem>
                        {roles.filter((r) => r.status === 'active').map((r) => (
                          <SelectItem key={r.id} value={r.id}>{formatMasterOptionLabel(r)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Default department</label>
                  <Select value={defaults.department_id || NONE} onValueChange={(v) => setDefaults({ ...defaults, department_id: v === NONE ? '' : v, topic: '' })}>
                    <SelectTrigger data-testid="bulk-default-dept"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None (use CSV column)</SelectItem>
                      {departments.filter((d) => d.status === 'active').map((d) => (
                        <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Default category</label>
                  <Select value={defaults.topic || NONE} onValueChange={(v) => setDefaults({ ...defaults, topic: v === NONE ? '' : v })} disabled={!defaults.department_id}>
                    <SelectTrigger data-testid="bulk-default-topic"><SelectValue placeholder={defaults.department_id ? 'None (use CSV column)' : 'Pick department first'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None (use CSV column)</SelectItem>
                      {workCategories.map((c) => (
                        <SelectItem key={c.id || c.label} value={c.label}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <div className="rounded-xl border border-dashed bg-secondary/30 p-4 text-center">
            <FileSpreadsheet className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-xs text-muted-foreground">{requiredColsHint}</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              data-testid={`${kind}-bulk-file`}
            />
            <Button type="button" variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => fileRef.current?.click()}>
              <Upload className="h-3.5 w-3.5" /> {file ? file.name : 'Choose CSV file'}
            </Button>
          </div>

          {preview && (
            <div className="rounded-lg border bg-card p-3" data-testid={`${kind}-bulk-preview`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Preview — {preview.valid_count} valid · {preview.invalid_count} invalid
              </p>
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs">
                {preview.rows.map((row) => (
                  <li
                    key={row.row_number}
                    className={`rounded-md border px-2 py-1.5 ${row.valid ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`}
                  >
                    <span className="font-mono text-[10px] text-muted-foreground">Row {row.row_number}</span>
                    <span className="ml-2 line-clamp-1">{row.text || '(empty)'}</span>
                    {!row.valid && row.errors?.length > 0 && (
                      <p className="mt-0.5 text-rose-600">{row.errors.join(' · ')}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => handleClose(false)}>Cancel</Button>
          <Button variant="outline" onClick={runPreview} disabled={!file || loading} data-testid={`${kind}-bulk-preview-btn`}>
            {loading ? 'Validating…' : 'Preview'}
          </Button>
          <Button onClick={runImport} disabled={!preview?.valid_count || importing} data-testid={`${kind}-bulk-import-btn`}>
            {importing ? 'Importing…' : `Import ${preview?.valid_count ?? 0} question${preview?.valid_count === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
